/**
 * <s-product-thumbs> — product gallery showing one photo at a time with thumbnails and dot indicators.
 * Desktop: clicking a thumbnail or dot swaps the single visible photo (others are hidden by CSS via [data-active]).
 * Mobile: photos swipe horizontally; tapping a thumbnail or dot scrolls to that photo, and swiping updates the current one.
 * Autoplay (2 Nov 2026): photos advance every 5 seconds; the current dot fills like a progress bar.
 * Pauses while the shopper hovers or focuses the gallery, while the tab is hidden, and is off for reduced motion.
 */
const AUTOPLAY_MS = 5000;

class SProductThumbs extends HTMLElement {
  connectedCallback() {
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.track = this.querySelector('[data-product-gallery]');
    if (this.slides.length < 2) return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.holds = new Set();

    this.onClick = (event) => {
      const control = event.target.closest('[data-thumb], [data-dot]');
      if (!control) return;
      const id = control.dataset.thumb || control.dataset.dot;
      if (!this.slideFor(id)) return;
      event.preventDefault();
      this.go(id);
    };
    this.addEventListener('click', this.onClick);

    // Swiping on phones: the photo mostly in view becomes current.
    if (this.track) {
      this.observer = new IntersectionObserver((entries) => {
        if (!this.isScroller()) return;
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible) this.setCurrent(visible.target.dataset.gallerySlide);
      }, { root: this.track, threshold: 0.6 });
      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    // Pause autoplay while the shopper is looking at or using the gallery.
    const gallery = this.track?.parentElement || this;
    this.onEnter = () => this.hold('hover');
    this.onLeave = () => this.release('hover');
    this.onFocusIn = () => this.hold('focus');
    this.onFocusOut = (event) => { if (!this.contains(event.relatedTarget)) this.release('focus'); };
    this.onVisibility = () => (document.hidden ? this.hold('hidden') : this.release('hidden'));
    gallery.addEventListener('pointerenter', this.onEnter);
    gallery.addEventListener('pointerleave', this.onLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.gallery = gallery;

    const first = this.slides.find((slide) => slide.hasAttribute('data-active')) || this.slides[0];
    this.setCurrent(first.dataset.gallerySlide, true);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.animation?.cancel();
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.gallery?.removeEventListener('pointerenter', this.onEnter);
    this.gallery?.removeEventListener('pointerleave', this.onLeave);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  slideFor(id) {
    return this.slides.find((slide) => slide.dataset.gallerySlide === id);
  }

  isScroller() {
    return this.track && this.track.scrollWidth > this.track.clientWidth + 1;
  }

  go(id) {
    const slide = this.slideFor(id);
    if (!slide) return;
    this.setCurrent(id);
    // On phones the track scrolls sideways; bring the chosen photo into view without moving the page.
    if (this.isScroller()) {
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: this.reduceMotion ? 'auto' : 'smooth' });
    }
  }

  next() {
    const index = this.slides.findIndex((slide) => slide.dataset.gallerySlide === this.currentId);
    const nextSlide = this.slides[(index + 1) % this.slides.length];
    this.go(nextSlide.dataset.gallerySlide);
  }

  setCurrent(id, force = false) {
    if (id === this.currentId && !force) return;
    this.currentId = id;
    this.thumbs.forEach((thumb) => thumb.setAttribute('aria-current', thumb.dataset.thumb === id ? 'true' : 'false'));
    this.dots.forEach((dot) => dot.setAttribute('aria-current', dot.dataset.dot === id ? 'true' : 'false'));
    this.slides.forEach((slide) => slide.toggleAttribute('data-active', slide.dataset.gallerySlide === id));
    this.startProgress();
  }

  // The current dot's fill grows over AUTOPLAY_MS; when it finishes the next photo shows.
  startProgress() {
    this.animation?.cancel();
    this.animation = null;
    if (this.reduceMotion) return;
    const dot = this.dots.find((d) => d.dataset.dot === this.currentId);
    const fill = dot?.querySelector('[data-dot-fill]');
    if (!fill || typeof fill.animate !== 'function') return;
    this.animation = fill.animate(
      [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
      { duration: AUTOPLAY_MS, easing: 'linear' }
    );
    this.animation.onfinish = () => this.next();
    if (this.holds.size) this.animation.pause();
  }

  hold(reason) {
    this.holds.add(reason);
    this.animation?.pause();
  }

  release(reason) {
    this.holds.delete(reason);
    if (!this.holds.size && this.animation?.playState === 'paused') this.animation.play();
  }
}

if (!customElements.get('s-product-thumbs')) customElements.define('s-product-thumbs', SProductThumbs);
